import { Check, Close } from '@flowio/react-icons';
import { noop, sortBy } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Grid, Column, Row } from '@flowio/react-grid';
import { OutlineButton } from '@flowio/react-button';
import { Box } from '@flowio/react-box';
import { Link } from 'react-router';
import {
  Card, CardHeader, CardContent, CardFooter, CardTitle,
} from '@flowio/react-card';
import { FraudStatus } from '@flowio/api-constants';
import { FraudProvider } from '@flowio/api-internal-constants';
import Description from '../../../../components/description/description';

import FormattedDate from '../../../../components/formatted-date';
import DescriptionGroup from '../../../../components/description/description-group';
import StatusLabel from '../../../../components/status-label';
import * as styles from './orderFraudStatus.styles';
import FraudReviewStatus from '../../constants/FraudReviewStatus';

const getStatusIntent = (status: io.flow.v0.enums.FraudStatus): string => {
  switch (status) {
    case 'review':
      return 'warning';
    case 'approved':
      return 'positive';
    case 'declined':
      return 'negative';
    default:
      return 'neutral';
  }
};

const getRiskEvaluation = (riskEvaluation: string | undefined): string => {
  if (riskEvaluation === 'none') {
    return 'Disabled per client request';
  }

  return riskEvaluation || '';
};

const getResponsibleParty = (organization: string, responsibleParty?: string): string => {
  switch (responsibleParty) {
    case 'organization':
      return organization;
    case 'flow':
      return 'Flow';
    default:
      return organization;
  }
};

const getForterLink = (refId: string): string => {
  if (refId) {
    return `https://portal.forter.com/app/dashboard/transactions/id/${refId}/details`;
  }
  return '';
};

interface FraudReviewAuthorizationSectionProps {
  fra: io.flow.internal.v0.models.FraudAuthorizationSummary;
  isFlowEmployee: boolean;
  handleApprove: (fraudId: string) => void;
  handleDecline: (fraudId: string) => void;
}

const FraudReviewAuthorizationSection: React.FC<FraudReviewAuthorizationSectionProps> = ({
  fra,
  isFlowEmployee,
  handleApprove = noop,
  handleDecline = noop,
}) => (
  <Card>
    <CardContent>
      <Grid className={styles.fraudClearFix} gutter={false}>
        <Row gutter={false}>
          <Column span={6}>
            <DescriptionGroup spacing={false} className={''}>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Reference #" />
                <Description.Definition
                  content={fra.fraud_review_authorization.id || ''}
                />
              </Description>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Authorization #" />
                <Description.Definition
                  content={fra.fraud_review_authorization.payment_authorization_id || ''}
                />
              </Description>
              <Description
                term="Risk Evaluation"
                definition={getRiskEvaluation(fra.fraud_review_authorization.risk_evaluation)}
              >
                <Description.Term className={styles.fraudLabel} content="Risk Evaluation" />
                <Description.Definition
                  content={getRiskEvaluation(fra.fraud_review_authorization.risk_evaluation)}
                />
              </Description>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Status" />
                <Description.Definition>
                  <StatusLabel
                    content={fra.fraud_review_authorization.status}
                    intent={getStatusIntent(fra.fraud_review_authorization.status)}
                  />
                </Description.Definition>
              </Description>
            </DescriptionGroup>
          </Column>
          <Column span={6}>
            <DescriptionGroup spacing={false} className={''}>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Last Updated" />
                <Description.Definition>
                  <FormattedDate utc value={fra.fraud_review_authorization.status_updated_at} format="MMM DD, YYYY h:mm A" />
                </Description.Definition>
              </Description>
              {fra.fraud_review_authorization.provider === FraudProvider.FORTER && (
              <Description>
                <Description.Term className={styles.fraudLabel} content={'Forter Link'} />
                <Description.Definition>
                  <Link to={{ pathname: getForterLink(fra.fraud_review_authorization.id) }} target={'_blank'}>
                    {fra.fraud_review_authorization.id}
                  </Link>
                </Description.Definition>
              </Description>
              )}
              <Description term="Description" definition={fra.fraud_review_authorization.description}>
                <Description.Term className={styles.fraudLabel} content="Description" />
                <Description.Definition content={fra.fraud_review_authorization.description} />
              </Description>
            </DescriptionGroup>
          </Column>
        </Row>
        { (fra.fraud_review_authorization.status === FraudStatus.REVIEW && isFlowEmployee) && (
        <div className={styles.reviewSection}>
          <Box spacing="loose">
            <OutlineButton
              className={styles.fraudReviewButton}
              disabled={!fra.fraud_pending_review_authorization}
              content="Approve Order"
              intent="primary"
              leftIcon={Check}
              onClick={() => handleApprove(fra.fraud_review_authorization.id)}
            />
            <OutlineButton
              className={styles.fraudReviewButton}
              disabled={!fra.fraud_pending_review_authorization}
              content="Deny Order"
              intent="negative"
              leftIcon={Close}
              onClick={() => handleDecline(fra.fraud_review_authorization.id)}
            />
          </Box>
          <div className={styles.fraudReviewMessage}>
            { fra.fraud_pending_review_authorization ? (
              <span>
                By manually approving or declining the order,&nbsp;
                <span className={styles.fraudResponsibleParty}>
                  {
                    getResponsibleParty(
                      fra.fraud_review_authorization.organization_id,
                      fra.fraud_review_authorization.responsible_party || 'flow',
                    )
                  }
                </span>
              &nbsp;assumes the risk of this order.
              </span>
            ) : (
              <span>
                Your decision has been submitted. Reload this page
                after a few seconds to view updated status.
              </span>
            )}
          </div>
        </div>
        )}
      </Grid>
    </CardContent>
  </Card>
);

interface OrderFraudProtectionProps {
  fraudReview?: io.flow.internal.v0.models.FraudReview,
  fraudReviewAuthorizations: io.flow.internal.v0.models.FraudAuthorizationSummary[],
  isFlowEmployee: boolean;
  handleApprove: (fraudId: string) => void;
  handleDecline: (fraudId: string) => void;
}

const OrderFraudProtection: React.FC<OrderFraudProtectionProps> = ({
  fraudReview = null,
  fraudReviewAuthorizations = [],
  isFlowEmployee = true,
  handleApprove = noop,
  handleDecline = noop,
}) => (
  <Card>
    <CardHeader dividing>
      <CardTitle content="Fraud Protection" />
    </CardHeader>
    <CardContent>
      <Grid className={styles.fraudClearFix} gutter={false}>
        <Row gutter={false}>
          <Column span={6}>
            <DescriptionGroup spacing={false} className={''}>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Reference #" />
                <Description.Definition
                  content={fraudReview?.id || ''}
                />
              </Description>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Overall Status" />
                <Description.Definition>
                  <StatusLabel
                    content={fraudReview?.status || FraudReviewStatus.PENDING}
                    intent={getStatusIntent(fraudReview?.status || FraudReviewStatus.PENDING)}
                  />
                </Description.Definition>
              </Description>
            </DescriptionGroup>
          </Column>
          <Column span={6}>
            <DescriptionGroup spacing={false} className={''}>
              <Description>
                <Description.Term className={styles.fraudLabel} content="Last Updated" />
                <Description.Definition>
                  {
                  fraudReviewAuthorizations.length
                  && sortBy(
                    fraudReviewAuthorizations
                      .map(
                        (fra) => fra.fraud_review_authorization.status_updated_at
                          || fra.fraud_review_authorization.created_at,
                      )
                      .filter((date) => date),
                    [(date) => moment(date).unix()],
                  )
                    .map(
                      (date) => (
                        <FormattedDate
                          utc
                          key={date?.toString()}
                          value={date}
                          format="MMM DD, YYYY h:mm A"
                        />
                      ),
                    ).slice(-1)
                }
                </Description.Definition>
              </Description>
              {
              // A workaround due to not knowing if forter is
              // linked via the frd or fra (display both - one will work)
              // To be updated upon switchover to multiple FRA per order
              fraudReview?.provider === FraudProvider.FORTER && (
                <Description>
                  <Description.Term className={styles.fraudLabel} content={'Forter Link'} />
                  <Description.Definition>
                    <Link to={{ pathname: getForterLink(fraudReview.id) }} target={'_blank'}>
                      {fraudReview.id}
                    </Link>
                  </Description.Definition>
                </Description>
              )
            }
            </DescriptionGroup>
          </Column>
        </Row>
        {
        fraudReviewAuthorizations.length > 0 && (
          <CardHeader>
            <CardTitle content="Fraud Review Authorizations" />
          </CardHeader>
        )
      }
        {
        // Order FRAs oldest first
        sortBy(
          fraudReviewAuthorizations,
          [(fra) => moment(fra.fraud_review_authorization.created_at).unix()],
        )
          .map((fra) => (
            <FraudReviewAuthorizationSection
              key={fra.fraud_review_authorization.id}
              fra={fra}
              isFlowEmployee={isFlowEmployee}
              handleApprove={handleApprove}
              handleDecline={handleDecline}
            />
          ))
      }
      </Grid>
    </CardContent>
    <CardFooter />
  </Card>
);

OrderFraudProtection.displayName = 'OrderFraudProtection';

export default OrderFraudProtection;
